import { Close, ContentCopy } from "@mui/icons-material";
import {
  Alert,
  Box,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "shared/common/contexts/SnackbarProvider";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { generateShareURL } from "shared/common/utils/general-utils";
import PdfReport from "shared/ui/toolbar/tools/pdfReport/PdfReport";

export default function ShareContent({
  role,
}: {
  role?: "edit" | "view" | "api";
}) {
  const { t } = useTranslation();

  const darkTheme = defaultStore((state) => state.darkTheme);
  const viewer = defaultStore((state) => state.viewer);
  const authMode = defaultStore((state) => state.authMode);
  const { showMessage } = useSnackbar();

  const [shareURL, setShareURL] = useState<URL>();
  const [remark, setRemark] = useState<string>();

  const profileToolValues = sidebarStore((state) => state.profileToolValues);
  const changeSharePopover = sidebarStore((state) => state.changeSharePopover);
  const sharePopover = sidebarStore((state) => state.sharePopover);

  const error = (remark && remark?.length > 500) || false;

  const handleClose = () => {
    changeSharePopover(false);
  };

  // when view configuration change, update shareURL
  useEffect(() => {
    if (!viewer || !sharePopover) return;
    if (role !== "api") {
      updateShareURL();

      const handleCameraConfigChange = () => updateShareURL();
      const handleGeoFrameChange = () => updateShareURL();
      const handlePanoRotationChange = () => updateShareURL();

      viewer.on("cameraconfigurationchanged", handleCameraConfigChange);
      viewer.on("geoframechanged", handleGeoFrameChange);
      viewer.on("panorotationchanged", handlePanoRotationChange);

      return () => {
        if (!viewer) return;
        viewer.off("cameraconfigurationchanged", handleCameraConfigChange);
        viewer.off("geoframechanged", handleGeoFrameChange);
        viewer.off("panorotationchanged", handlePanoRotationChange);
      };
    }
  }, [viewer, sharePopover, role]);

  function updateShareURL() {
    if (!viewer) return;
    const imageKey = viewer.getScene().getCurrentGeoframe()?.key;
    const cameraViewString = JSON.stringify(viewer.getCameraView());
    const url = generateShareURL(imageKey, cameraViewString);
    setShareURL(url);
  }

  return (
    <Box height="100%" display={"flex"} flexDirection="column">
      <Box
        gap={0.5}
        paddingX={1.5}
        paddingY={1.5}
        sx={{
          height: "100%",
          overflow: "hidden",
          backgroundColor: darkTheme ? "darkblue.main" : "white",
          borderRadius: "10px",
          transition: "height 0.3s , margin 0.3s , padding 0.3s",
        }}
      >
        <Box
          display={"flex"}
          flexDirection="row"
          gap={1}
          alignItems="center"
          sx={{
            color: darkTheme ? "white" : "darkblue.main",
          }}
        >
          <Typography
            fontWeight={"600"}
            noWrap
            marginRight={"auto"}
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {t("share_view.share")}
          </Typography>

          <IconButton
            sx={{
              padding: 0,
              marginY: 1,
              color: darkTheme ? "white" : "darkblue.main",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main" },
              },
            }}
            onClick={() => handleClose()}
            disableRipple={true}
          >
            <Close />
          </IconButton>
        </Box>

        <Box
          marginTop={"0px"}
          zIndex={1}
          width="100%"
          gap={1}
          sx={{
            backgroundColor: darkTheme ? "darkblue.main" : "white",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
            transition: "margin 0.3s",
            position: "relative",
          }}
        >
          {role !== "api" && (
            <TextField
              size="small"
              id="shareLink"
              label="Link"
              value={shareURL ?? ""}
              sx={{
                marginBlockStart: 1,
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                },
                "& .MuiInputBase-root": {
                  color: darkTheme ? "white" : "darkblue.main",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main",
                  },
                  "&:hover fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: darkTheme ? "white" : "darkblue.main",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: darkTheme ? "white" : "darkblue.main",
                },
              }}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      if (shareURL) {
                        showMessage(
                          t("share_view.link_copied_message"),
                          "success"
                        );
                        navigator.clipboard.writeText(shareURL.toString());
                      }
                    }}
                  >
                    <ContentCopy
                      sx={{ color: darkTheme ? "white" : "darkblue.main" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Divider
            sx={{
              marginBlock: 1,
              borderColor: darkTheme ? "white" : "darkblue.main",
              opacity: "0.12",
            }}
          />
          <Typography
            fontWeight={"600"}
            noWrap
            marginRight={"auto"}
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
            }}
          >
            {t("share_view.pdf_report")}
          </Typography>
          <Typography
            fontWeight={"400"}
            variant="body2"
            sx={{
              color: darkTheme ? "white" : "darkblue.main",
              transition: "margin ease-in-out 0.2s",
            }}
          >
            {authMode === "view"
              ? t("share_view.pdf_report_description_view")
              : t("share_view.pdf_report_description")}
          </Typography>
          <FormControl error={error}>
            <TextField
              size="small"
              id="remark"
              label={t("share_view.remarks")}
              multiline
              rows={4}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              error={error}
              helperText={error && t("share_view.remarks_too_long_error")}
              sx={{
                marginBlockStart: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main", // default
                  },
                  "&:hover fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main", // hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: darkTheme ? "white" : "darkblue.main", // focus
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "#d32f2f", // red on error
                  },
                },
                "& .MuiInputLabel-root": {
                  color: darkTheme ? "white" : "darkblue.main",
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: darkTheme ? "white" : "darkblue.main",
                },
                "& .MuiInputLabel-root.Mui-error": {
                  color: "#d32f2f", // optional: red label on error
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: darkTheme ? "white" : "darkblue.main",
                },
              }}
            />
          </FormControl>
          {profileToolValues ? (
            <Alert severity="warning">
              {t("share_view.pdf_report_profile_error")}
            </Alert>
          ) : (
            <Box sx={{ display: "grid", justifyContent: "right" }}>
              <PdfReport
                buttonDisabled={error}
                remark={remark}
                shareURL={shareURL?.toString()}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
